/**
 * Page overlay: opens product (and optionally collection) links in a panel that slides over the
 * current page instead of loading a new document. The address bar is updated with pushState, the
 * back button closes the panel, and a reload lands on the real page.
 *
 * The linked page is fetched whole and its `#MainContent` is rendered inside the panel. Theme
 * scripts that page loads but the current one does not (e.g. media-gallery.js, only emitted on
 * product templates) are appended so its components upgrade.
 *
 * Falls back to a normal navigation when the linked page renders sections that already exist on the
 * underlying page (product over product, collection over collection): duplicate section IDs would
 * make the Section Rendering API update the wrong copy.
 */

const LOCALE_PREFIX = "(?:/[a-z]{2}(?:-[a-z]{2,4})?)?";
const PRODUCT_PATH = new RegExp(
  `^${LOCALE_PREFIX}(?:/collections/[^/]+)?/products/[^/]+/?$`,
  "i",
);
const COLLECTION_PATH = new RegExp(
  `^${LOCALE_PREFIX}/collections/[^/]+/?$`,
  "i",
);
const EXECUTABLE_SCRIPT_TYPES = [
  "",
  "module",
  "text/javascript",
  "application/javascript",
];
const PREFETCH_DELAY_MS = 80;
const CACHE_LIMIT = 12;
/** How long the panel waits for its page before sliding in empty, showing the loading state. */
const SLIDE_WAIT_MS = 350;

class PageOverlay extends HTMLElement {
  /** Path of the document actually loaded in the browser (the page under the overlay). */
  #documentPath = window.location.pathname;
  /** Paths this overlay pushed onto the history stack, oldest first. */
  #stack = /** @type {string[]} */ ([]);
  #currentPath = "";
  /** URL of the underlying page when the overlay opened, query string included. */
  #documentHref = "";
  #documentTitle = "";
  #hiding = false;
  #requestId = 0;
  /** @type {Map<string, Promise<string>>} */
  #cache = new Map();
  /** @type {number | undefined} */
  #prefetchTimer;
  /** @type {number | undefined} */
  #entranceTimer;
  /** Whether the last input was a key press rather than a tap or click (see #hide). */
  #keyboardInput = false;

  get #dialog() {
    return /** @type {HTMLDialogElement} */ (this.querySelector("dialog"));
  }

  get #panel() {
    return /** @type {HTMLElement} */ (
      this.querySelector(".page-overlay__panel")
    );
  }

  get #content() {
    return /** @type {HTMLElement} */ (
      this.querySelector(".page-overlay__content")
    );
  }

  get #targets() {
    return this.dataset.targets === "products_and_collections"
      ? [PRODUCT_PATH, COLLECTION_PATH]
      : [PRODUCT_PATH];
  }

  get #reduceMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  get isOpen() {
    return this.#dialog.open;
  }

  connectedCallback() {
    // Capture phase: product cards navigate from their own click listener, so get there first.
    document.addEventListener("click", this.#handleDocumentClick, {
      capture: true,
    });
    document.addEventListener("pointerover", this.#handlePointerOver, {
      passive: true,
    });
    document.addEventListener("touchstart", this.#handlePointerOver, {
      passive: true,
    });
    window.addEventListener("popstate", this.#handlePopState);
    this.#dialog.addEventListener("cancel", this.#handleCancel);
    this.addEventListener("click", this.#handleOwnClick);
    document.addEventListener("pointerdown", this.#trackPointerInput, {
      capture: true,
      passive: true,
    });
    document.addEventListener("keydown", this.#trackKeyboardInput, {
      capture: true,
    });
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.#handleDocumentClick, {
      capture: true,
    });
    document.removeEventListener("pointerover", this.#handlePointerOver);
    document.removeEventListener("touchstart", this.#handlePointerOver);
    window.removeEventListener("popstate", this.#handlePopState);
    this.#dialog.removeEventListener("cancel", this.#handleCancel);
    this.removeEventListener("click", this.#handleOwnClick);
    document.removeEventListener("pointerdown", this.#trackPointerInput, {
      capture: true,
    });
    document.removeEventListener("keydown", this.#trackKeyboardInput, {
      capture: true,
    });
  }

  #trackPointerInput = () => {
    this.#keyboardInput = false;
  };

  #trackKeyboardInput = () => {
    this.#keyboardInput = true;
  };

  /**
   * Opens a URL in the overlay.
   * @param {URL} url
   * @param {{ push: boolean }} options - push a history entry (link click) or not (back/forward)
   */
  async open(url, { push }) {
    const requestId = ++this.#requestId;

    if (!this.isOpen) {
      // On a back/forward open the URL has already changed, so keep the last known underlying URL.
      if (push) this.#documentHref = window.location.href;
      this.#documentTitle = document.title;
    }

    if (push) {
      history.pushState({ pageOverlay: true }, "", url.href);
      this.#stack.push(url.pathname);
    }
    this.#currentPath = url.pathname;

    const request = this.#fetch(url);

    if (this.isOpen) {
      this.#panel.setAttribute("aria-busy", "true");
    } else {
      this.#show();
    }

    /** @type {Document} */
    let page;
    try {
      page = new DOMParser().parseFromString(await request, "text/html");
    } catch {
      if (requestId === this.#requestId) window.location.replace(url.href);
      return;
    }

    // A newer open() or a close() superseded this one while it was loading.
    if (requestId !== this.#requestId) return;

    const main = page.getElementById("MainContent");
    if (!main || this.#collidesWithDocument(main)) {
      window.location.replace(url.href);
      return;
    }

    this.#appendMissingAssets(page);

    const content = this.#content;
    content.dataset.template = main.dataset.template ?? "";
    content.innerHTML = main.innerHTML;
    this.#activateScripts(content);

    document.title = page.title;

    this.#panel.scrollTo({ top: 0, behavior: "instant" });
    this.#panel.removeAttribute("aria-busy");

    // A frame for the new page's first layout, so it doesn't land on the slide's first frames.
    requestAnimationFrame(() => this.#playEntrance());
  }

  /**
   * Closes the overlay by rewinding the history entries it pushed; popstate does the hiding.
   */
  close() {
    if (!this.isOpen || this.#hiding) return;

    if (this.#stack.length > 0) {
      history.go(-this.#stack.length);
    } else {
      // Opened by a forward navigation: no entries of ours to rewind, so restore the URL in place.
      history.pushState(null, "", this.#documentHref || this.#documentPath);
      this.#hide();
    }
  }

  /**
   * Opens the dialog held invisible (`data-held`: panel and backdrop at opacity 0). The page is
   * rendered into the open panel, where its components can measure themselves, and the slide
   * plays once it is in, so the panel arrives full. A slow page slides in anyway after
   * SLIDE_WAIT_MS, showing the loading state.
   *
   * The hold is CSS, not a slide paused at its first frame: Safari paints a paused animation's
   * element at its resting place, so the panel flashed up empty and then slid in again.
   */
  #show() {
    const dialog = this.#dialog;

    this.#panel.setAttribute("aria-busy", "true");
    document.documentElement.classList.add("page-overlay-open");
    this.toggleAttribute("data-held", true);
    dialog.showModal();

    this.#entranceTimer = window.setTimeout(
      () => this.#playEntrance(),
      SLIDE_WAIT_MS,
    );
  }

  #playEntrance() {
    window.clearTimeout(this.#entranceTimer);
    if (!this.hasAttribute("data-held")) return;

    // Started in the same task the hold lifts, so the first painted frame is the slide's first.
    this.#animate("in");
    this.removeAttribute("data-held");
  }

  async #hide() {
    if (this.#hiding) return;
    this.#hiding = true;
    this.#requestId++;
    this.#stack = [];
    this.#currentPath = "";

    if (this.#documentTitle) document.title = this.#documentTitle;

    // Closed while the panel was still held: it never showed, so there is nothing to slide out.
    const neverShown = this.hasAttribute("data-held");
    window.clearTimeout(this.#entranceTimer);

    const animations = neverShown ? [] : this.#animate("out");
    await Promise.all(
      animations.map((animation) => animation.finished.catch(() => {})),
    );

    this.#dialog.close();

    // Closing hands focus back to what had it before the panel opened, on the page behind. After
    // a tap or click, WebKit (every iOS browser) counts that as visible focus and rings the
    // product card, so let it go then; keyboard users keep their place.
    if (!this.#keyboardInput && document.activeElement instanceof HTMLElement)
      document.activeElement.blur();

    this.removeAttribute("data-held");
    animations.forEach((animation) => animation.cancel());
    document.documentElement.classList.remove("page-overlay-open");
    this.#content.replaceChildren();
    this.#panel.removeAttribute("aria-busy");
    this.#hiding = false;
  }

  /**
   * Where the panel slides in from: the bottom edge on mobile when set to, else its side.
   */
  get #slideFrom() {
    if (
      this.dataset.positionMobile === "bottom" &&
      !window.matchMedia("(min-width: 750px)").matches
    ) {
      return "0 100%";
    }
    return this.dataset.position === "left" ? "-100% 0" : "100% 0";
  }

  /**
   * @param {'in' | 'out'} direction
   * @returns {Animation[]}
   */
  #animate(direction) {
    const style = this.dataset.animation;
    if (style === "none" || this.#reduceMotion) return [];

    const duration = Number(this.dataset.duration) || 600;
    const panelFrames =
      style === "fade"
        ? [{ opacity: 0 }, { opacity: 1 }]
        : [{ translate: this.#slideFrom }, { translate: "0 0" }];

    /** @type {KeyframeAnimationOptions} */
    const options = {
      // Drawer-like swoop: long ease-out on the way in, a quicker ease-in on the way out.
      duration: direction === "in" ? duration : duration * 0.6,
      easing:
        direction === "in"
          ? "cubic-bezier(0.22, 1, 0.36, 1)"
          : "cubic-bezier(0.5, 0, 0.75, 0)",
      direction: direction === "in" ? "normal" : "reverse",
      fill: direction === "in" ? "none" : "forwards",
    };

    const animations = [this.#panel.animate(panelFrames, options)];
    const backdrop = this.querySelector(".page-overlay__backdrop");
    if (backdrop)
      animations.push(backdrop.animate([{ opacity: 0 }, { opacity: 1 }], options));

    return animations;
  }

  /**
   * @param {string} href
   * @returns {URL | null} the URL when it should open in the overlay
   */
  #eligibleUrl(href) {
    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return null;
    }

    if (url.origin !== window.location.origin) return null;
    if (!this.#targets.some((pattern) => pattern.test(url.pathname)))
      return null;

    return url;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    if (!(event.target instanceof Element)) return;

    const link = this.#linkForClick(event.target);
    if (!link) return;
    if (
      (link.target && link.target !== "_self") ||
      link.hasAttribute("download")
    )
      return;
    if (link.dataset.pageOverlay === "false") return;

    // Links inside other open dialogs (cart drawer, search, quick add) navigate normally.
    const dialog = link.closest("dialog");
    if (dialog && dialog !== this.#dialog) return;

    const url = this.#eligibleUrl(link.href);
    if (!url) return;

    // The page underneath, or another page of the overlaid one (pagination): navigate normally.
    if (
      url.pathname === (this.isOpen ? this.#currentPath : this.#documentPath)
    ) {
      if (url.search === window.location.search) event.preventDefault();
      return;
    }

    event.preventDefault();
    this.open(url, { push: true });
  };

  /**
   * The link a click navigates through: the clicked anchor, or the product card's main link when
   * the click landed on the card itself (product-card.js navigates those clicks by script).
   * @param {Element} target
   * @returns {HTMLAnchorElement | null}
   */
  #linkForClick(target) {
    const anchor = target.closest("a[href]");
    if (anchor instanceof HTMLAnchorElement) return anchor;

    const card = target.closest("product-card");
    if (!card || card.hasAttribute("data-no-navigation")) return null;
    if (target.closest('button, input, label, select, [tabindex="1"]'))
      return null;

    const cardLink = card.querySelector('a[ref="productCardLink"]');
    return cardLink instanceof HTMLAnchorElement ? cardLink : null;
  }

  #handlePopState = () => {
    const url = new URL(window.location.href);

    if (!this.isOpen) {
      // Forward navigation back into an overlay entry: the underlying page is the entry before it.
      if (url.pathname !== this.#documentPath && this.#eligibleUrl(url.href)) {
        this.#stack = [url.pathname];
        this.open(url, { push: false });
      }
      return;
    }

    if (url.pathname === this.#documentPath) {
      this.#hide();
      return;
    }

    // Variant or filter changes rewrite the query string of the overlaid page; nothing to do.
    if (url.pathname === this.#currentPath) return;

    if (!this.#eligibleUrl(url.href)) {
      window.location.reload();
      return;
    }

    const index = this.#stack.indexOf(url.pathname);
    if (index === -1) {
      this.#stack.push(url.pathname);
    } else {
      this.#stack.length = index + 1;
    }

    this.open(url, { push: false });
  };

  /**
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleOwnClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest(".page-overlay__close, .page-overlay__backdrop"))
      this.close();
  };

  /**
   * Warms the cache when the pointer rests on an eligible link.
   * @param {Event} event
   */
  #handlePointerOver = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest("a[href]");
    if (!(link instanceof HTMLAnchorElement)) return;

    window.clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = window.setTimeout(() => {
      const url = this.#eligibleUrl(link.href);
      if (url && url.pathname !== this.#documentPath)
        this.#fetch(url).catch(() => {});
    }, PREFETCH_DELAY_MS);
  };

  /**
   * @param {URL} url
   * @returns {Promise<string>}
   */
  #fetch(url) {
    const key = url.href;
    const cached = this.#cache.get(key);
    if (cached) return cached;

    const request = fetch(key, { credentials: "same-origin" }).then(
      (response) => {
        // Redirects (password page, renamed handles) land somewhere the overlay should not render.
        const landed = new URL(response.url);
        if (
          !response.ok ||
          landed.pathname.replace(/\/$/, "") !== url.pathname.replace(/\/$/, "")
        ) {
          throw new Error(`Page overlay could not load ${key}`);
        }
        return response.text();
      },
    );

    request.catch(() => this.#cache.delete(key));
    this.#cache.set(key, request);
    if (this.#cache.size > CACHE_LIMIT) {
      const oldest = this.#cache.keys().next().value;
      if (oldest) this.#cache.delete(oldest);
    }

    return request;
  }

  /**
   * @param {HTMLElement} main - the fetched page's main element
   */
  #collidesWithDocument(main) {
    for (const section of main.querySelectorAll('[id^="shopify-section-"]')) {
      const existing = document.getElementById(section.id);
      if (existing && !this.contains(existing)) return true;
    }
    return false;
  }

  /**
   * Adds theme scripts and stylesheets the fetched page uses but this document has not loaded.
   * Limited to theme assets so app and analytics scripts are never initialised twice.
   * @param {Document} page
   */
  #appendMissingAssets(page) {
    const assetBase = new URL(
      this.dataset.assetBase ?? "",
      window.location.href,
    ).href;
    const isThemeAsset = (/** @type {string} */ src) =>
      new URL(src, window.location.href).href.startsWith(assetBase);

    const loadedScripts = new Set(
      [...document.scripts].map((script) => script.src).filter(Boolean),
    );
    for (const script of page.querySelectorAll("script[src]")) {
      if (
        !(script instanceof HTMLScriptElement) ||
        script.closest("#MainContent")
      )
        continue;

      const src = new URL(
        script.getAttribute("src") ?? "",
        window.location.href,
      ).href;
      if (loadedScripts.has(src) || !isThemeAsset(src)) continue;

      document.body.append(this.#cloneScript(script));
      loadedScripts.add(src);
    }

    const loadedStyles = new Set(
      [...document.querySelectorAll('link[rel="stylesheet"]')].map(
        (link) => /** @type {HTMLLinkElement} */ (link).href,
      ),
    );
    for (const link of page.querySelectorAll('link[rel="stylesheet"]')) {
      const href = new URL(
        link.getAttribute("href") ?? "",
        window.location.href,
      ).href;
      if (loadedStyles.has(href) || !isThemeAsset(href)) continue;

      const stylesheet = document.createElement("link");
      stylesheet.rel = "stylesheet";
      stylesheet.href = href;
      document.head.append(stylesheet);
      loadedStyles.add(href);
    }
  }

  /**
   * Scripts inserted through innerHTML never run; swap each executable one for a fresh copy.
   * @param {HTMLElement} container
   */
  #activateScripts(container) {
    const loadedScripts = new Set(
      [...document.scripts].map((script) => script.src).filter(Boolean),
    );

    for (const script of container.querySelectorAll("script")) {
      if (!EXECUTABLE_SCRIPT_TYPES.includes(script.type)) continue;
      if (
        script.src &&
        script.type !== "module" &&
        loadedScripts.has(script.src)
      ) {
        script.remove();
        continue;
      }
      script.replaceWith(this.#cloneScript(script));
    }
  }

  /**
   * @param {HTMLScriptElement} script
   */
  #cloneScript(script) {
    const clone = document.createElement("script");
    for (const { name, value } of script.attributes)
      clone.setAttribute(name, value);
    clone.textContent = script.textContent;
    return clone;
  }
}

if (!customElements.get("page-overlay")) {
  customElements.define("page-overlay", PageOverlay);
}
